import {Banner, Label, LoadingSpinner, Text, Toggle} from '@albert-io/atomic';
import classNames from 'classnames';
import React, {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useQuestionTranslationStore} from './TranslationStore';

import './translation-toggle.scss';

interface TranslationHeaderProps {
  showTranslations: boolean;
  onToggle: () => void;
  translationCoverage?: 'none' | 'full' | 'partial';
  fetchingTranslations?: boolean; // TODO: implment loading symbol or something
  inLegacyView?: boolean;
  children?: any;
}

const TranslationHeader = ({
  showTranslations,
  translationCoverage = 'none',
  inLegacyView = false,
  children,
  ...itemProps
}: TranslationHeaderProps) => {
  const noTranslation = translationCoverage === 'none';

  return (
    <Label
      className='translation-toggle'
      tabIndex={0}
      onClick={(e) => noTranslation && e.preventDefault()}
    >
      <Banner
        type={showTranslations ? 'info' : 'neutral'}
        cardClassName={classNames('translation-toggle__card', {
          'translation-toggle__card--legacy': inLegacyView,
          'pointer-events-none': noTranslation
        })}
        border={false}
      >
        <TranslationBannerItems
          {...itemProps}
          showTranslations={showTranslations}
          translationCoverage={translationCoverage}
        />
      </Banner>
      {children}
    </Label>
  );
};

const TranslationBannerItems = ({
  fetchingTranslations,
  translationCoverage,
  showTranslations,
  onToggle
}: Omit<TranslationHeaderProps, 'inLegacyView'>) => {
  if (fetchingTranslations) {
    return <LoadingSpinner bottomMarginLevel={0} className='mx-auto' />;
  }
  const noTranslation = translationCoverage === 'none';
  const hasSubText = translationCoverage === 'partial';
  const mainText = translationCoverage === 'none' ? translationText.none : translationText.es;
  const mainTextColor = translationCoverage === 'none' ? 'tertiary' : 'info';

  return (
    <div className='flex w-full items-center'>
      <Banner.Icon className='translation-header__no-top-icon' icon={['fal', 'language']} />
      <Text size='s' color={showTranslations ? mainTextColor : undefined}>
        {mainText}
      </Text>
      {hasSubText && (
        <Text size='s' className='ml-auto' italic color={showTranslations ? 'inherit' : 'tertiary'}>
          {translationText.partial}
        </Text>
      )}
      <Toggle
        size='xs'
        disabled={noTranslation}
        checked={showTranslations}
        className={hasSubText ? togglePadding.subtext : togglePadding.noSubtext}
        onChange={onToggle}
      />
    </div>
  );
};

const translationText = {
  none: 'Translations not available for this section',
  es: 'Translate to Spanish',
  partial: 'Partially available'
};

const togglePadding = {
  subtext: 'ml-x1',
  noSubtext: 'ml-auto'
};

type TranslationWrapperProps = Omit<
  TranslationHeaderProps,
  'translationCoverage' | 'showTranslations' | 'onToggle'
>;

const TranslationHeaderWrapper = (props: TranslationWrapperProps) => {
  const {fetchingTranslations, translationCoverage, showTranslations, setShowTranslations} =
    useQuestionTranslationStore(
      useShallow((state) => ({
        fetchingTranslations: state.fetchingTranslations,
        translationCoverage: state.translationCoverage,
        showTranslations: state.showTranslations,
        setShowTranslations: state.onToggle
      }))
    );

  return (
    <>
      <TranslationHeader
        showTranslations={showTranslations}
        onToggle={setShowTranslations}
        inLegacyView={props.inLegacyView}
        translationCoverage={translationCoverage}
        fetchingTranslations={fetchingTranslations}
      />
      {showTranslations && (
        <Banner
          className='translation-toggle--warning'
          border={false}
          type='warning'
          cardClassName={classNames('translation-toggle__card--warning', {
            'translation-toggle__card--warning--legacy': props.inLegacyView
          })}
        >
          <Banner.Icon className='!ml-auto' icon={['fal', 'hand']} />
          <Text color='inherit' size='s' className='mr-auto'>
            {warningText}
          </Text>
        </Banner>
      )}
    </>
  );
};

const warningText =
  'La respuesta se calificará según el texto en inglés; revisa tus respuestas en inglés.';

export default TranslationHeaderWrapper;

export const useShowTranslationToggle = () => {
  const {isReady, hasTranslationAccess, useAssignmentSettings, assignmentSettings} =
    useQuestionTranslationStore(
      useShallow((state) => ({
        isReady: state.isReady,
        hasTranslationAccess: state.hasTranslationAccess,
        useAssignmentSettings: state.useAssignmentSettings,
        assignmentSettings: state.assignmentSettings
      }))
    );
  const showTranslationToggle = useMemo(() => {
    if (isReady && hasTranslationAccess) {
      if (!useAssignmentSettings) {
        // don't worry about assignment settings if flag is off
        return true;
      }
      const {assignment, classroom, student} = assignmentSettings;

      return hasAssignmentSettingAccess(assignment, classroom, student);
    }
    return false;
  }, [isReady, hasTranslationAccess, useAssignmentSettings, assignmentSettings]);

  return showTranslationToggle;
};

export const hasAssignmentSettingAccess = (
  assignment: boolean,
  classroom: boolean,
  student: boolean
) => {
  // all translation settings are on
  if (assignment && classroom && student) {
    return true;
  }
  // has class overide to true, and student not set to false
  if (!assignment && classroom && student) {
    return true;
  }
  // has student override set to true
  if ((!assignment && !classroom && student) || (assignment && !classroom && student)) {
    return true;
  }
  return false;
};
